import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { AddStaffService } from '../../services/add-staff.service';
import { MatTableDataSource } from '@angular/material/table';
import { tap, flatMap, filter, toArray, map, switchMap } from 'rxjs/operators';
import { GoogleAnalyticsEvents } from '../../../../../models/GoogleAnalyticsEvents';
import { GoogleAnalyticsService } from '../../../../../services/googleanalytics.service';
import { OnBoardingService } from 'src/app/on-boarding/services/on-boarding.service';
import { StaffService } from 'src/app/services/staff.service';
import { StaffMemberUpload } from 'src/app/on-boarding/models/StaffMemberUpload';
import { StaffManagementService } from 'src/app/services/staff-management.service';
import { cloneDeep } from 'lodash';
import { ShiftTypesService } from 'src/app/services/shift-types/shift-types.service';
import { of } from 'rxjs';

@Component({
  selector: 'app-manual-add',
  templateUrl: './manual-add.component.html',
  styleUrls: ['./manual-add.component.scss'],
})
export class ManualAddComponent implements OnInit {
  @Input() showAddAnother: boolean;
  @Input() shownInApp: boolean;
  @Output() validState: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() activeStaff: EventEmitter<StaffMemberUpload[]> = new EventEmitter<
    StaffMemberUpload[]
  >();
  isAddStepp = true;
  allStaff: StaffMemberUpload[] = [];
  dataSource = new MatTableDataSource();
  staffTypes = [];
  staffForm: FormGroup = new FormGroup({
    staffArray: new FormArray([]),
  });
  displayedColumns: string[] = [
    'firstname',
    'lastname',
    'phonenumber',
    'stafftypeid',
    'action',
  ];
  hasBeenModified = false;
  isNewStaff = false;
  validStaff = false;
  editableStaffId = null;
  canDelete = true;
  editableMode = false;
  private _analyticsEvents = new GoogleAnalyticsEvents();
  private localStorageKey = 'manualStaff';

  constructor(
    private _addStaffService: AddStaffService,
    private _analyticsService: GoogleAnalyticsService,
    private _onBoardingService: OnBoardingService,
    private _staffService: StaffService,
    private _staffManagementService: StaffManagementService,
    private _shiftTypesService: ShiftTypesService
  ) { }

  ngOnInit(): void {
    this.uploadDataTable();
    this._getStaffTypes().subscribe((staffTypes) => {
      this.staffTypes = staffTypes,
        this.sortStaffList();
    });
  }

  createForm(): void {
    const formArray: FormArray = <FormArray>this.staffForm.get('staffArray');
    formArray.controls = [];
    this.allStaff
      .filter((e) => !e.deleted)
      .forEach((staff) => {
        formArray.push(
          new FormGroup({
            id: new FormControl(staff.id),
            firstname: new FormControl(staff.firstname, Validators.required),
            lastname: new FormControl(staff.lastname, Validators.required),
            phonenumber: new FormControl(
              staff.phonenumber,
              Validators.required
            ),
            stafftypeid: new FormControl(
              staff.stafftypeid,
              Validators.required
            ),
            deleted: new FormControl(staff.deleted),
            updated: new FormControl(staff.updated)
          })
        );
      });
  }

  private _updateGoogleAnalytics() {
    this._analyticsService.eventEmitter(
      this._analyticsEvents.onboarding.category,
      this._analyticsEvents.onboarding.action.manual_add_staff.name,
      this._analyticsEvents.onboarding.action.manual_add_staff.label
    );
  }

  getEditableStaffId(row) {
    const emptyRow = this.allStaff.find(a => !a.id);
    if (emptyRow) {
      this.cancelAction(emptyRow)
    }
    this.canDelete = true;

    this.editableMode = true;
    this.editableStaffId = row.id;
    this.isNewStaff = false;
    this.validState.emit(false);
    //Removes all new staffTypes that were  added 
    // this.staffTypes = [...this.staffTypes.filter(staffType => staffType.stafftypeid > 0)]

  }

  private _getStaffTypes() {
    return this._staffService.getStaffTypes().pipe(
      flatMap((e) => e),
      filter((a) => a.canadd === true && a.hidden === false),
      toArray()
    );
  }

  addNewRow(): void {
    const newRow: StaffMemberUpload = {
      id: null,
      firstname: '',
      lastname: '',
      phonenumber: '',
      stafftypeid: null,
      deleted: false,
      updated: false,
    };
    this.allStaff.push(newRow);
    this.isNewStaff = true;
    this.validStaff = false;
    this.createForm();
    this.editableStaffId = null;
    this.editableMode = true;
    this.validState.emit(false)
    this.canDelete = false;
  }

  addNewStaff(row) {
    const data = row.value;
    const indx = this.allStaff.findIndex((e) => !e.id);
    const staffType$ = data.stafftypeid < 0 ? this.addNewStaffType(data) : of(data.stafftypeid);
    staffType$.pipe(
      map(res => ({
        firstname: data.firstname,
        lastname: data.lastname,
        phonenumber: data.phonenumber,
        stafftypeid: res,
      })),
      switchMap(requestObj => this._addStaffService.manualAddStaff(requestObj)),
      tap((e) => this._updateGoogleAnalytics())
    ).subscribe((e) => {
      this.staffTypes = [...this.staffTypes.filter(staffType => staffType.stafftypeid > 0)]
      row.setValue(data)

      this.allStaff[indx] = {
        firstname: data.firstname,
        lastname: data.lastname,
        phonenumber: data.phonenumber,
        stafftypeid: data.stafftypeid,
        deleted: false,
        updated: false,
        id: e.data.staffid,
      };
      this.activeStaff.emit(this.allStaff);
      this.updateTable(this.allStaff);
    });
    this.canDelete = false;
    this.validStaff = false;
    this.isNewStaff = false;
  }

  updateStaff(data) {
    const staffType$ = data.stafftypeid < 0 ? this.addNewStaffType(data) : of(data.stafftypeid);
    staffType$.subscribe(res => {
      const indx = this.allStaff.findIndex((e) => e.id === data.id);
      data.stafftypeid = res;
      this.allStaff[indx] = data;
      this.allStaff[indx].updated = true;
      this.updateTable(this.allStaff);
      this.activeStaff.emit(this.allStaff);
      this.validStaff = false;
      this.hasBeenModified = false;
      this.staffTypes = [...this.staffTypes.filter(staffType => staffType.stafftypeid > 0)]
      this.sortStaffList();
    })
  }

  deleteStaff(data) {
    const indx = this.allStaff.findIndex((e) => e.id === data.id);
    this.allStaff[indx].deleted = true;
    this.allStaff[indx].updated = false;
    this.activeStaff.emit(this.allStaff);
    this.updateTable(this.allStaff);
  }

  cancelAction(row) {
    if (!row.id) {
      this.allStaff.pop();
      this.isNewStaff = false;
    }
    this.staffTypes = [...this.staffTypes.filter(staffType => staffType.stafftypeid > 0)]

    this.updateTable(this.allStaff);
  }

  updateTable(data) {
    this.dataSource.data = data.filter(e => !e.deleted);
    this.editableMode = false;
    if (this.dataSource.data.length === 0 && this.showAddAnother) {
      this.editableMode = true;
      this.addNewRow();
    }
    this.hasBeenModified = false;
    this.editableStaffId = null;
    this.createForm();
    if (!this.shownInApp) {
      this._onBoardingService.savePartialInfoInLs(
        this.localStorageKey,
        this.allStaff
      );
    }

    this.validState.emit(this.allStaff.some((e) => !e.deleted) && data.length > 0 && !this.allStaff.find(e => !e.id));
  }

  showStaffTypeAbbreviation(id) {
    const indx = this.staffTypes.findIndex((i) => i.stafftypeid === id);
    return this.staffTypes[indx]?.abbreviation;
  }

  onInput(data) {
    const indx = this.allStaff.findIndex((e) => e.id === data.value.id);
    if (!this.isNewStaff) {
      this.hasBeenModified =
        !this._onBoardingService._checkObjects(
          this.allStaff[indx],
          data.value
        ) && data.status === 'VALID';
    } else {
      this.validStaff = data.status === 'VALID' ? true : false;
    }
    this.validState.emit(false);
  }

  private _getStaff() {
    return this._staffManagementService
      .getAllStaff({ startDate: '', endDate: '' })
      .pipe(
        filter((item) => !!item.data),
        flatMap((item) => item.data),
        filter((item) => !!item.active && item.agencyName !== "IntelyCare"),
        map((item) => ({
          id: item.staffId,
          firstname: item.firstName,
          lastname: item.lastName,
          phonenumber: item.phoneNumber,
          stafftypeid: item.staffTypeId,
          updated: false,
          deleted: false,
        })),
        toArray()
      );
  }

  uploadDataTable() {
    this._getStaff().subscribe((staffList) => {
      const existingStaff = this._onBoardingService.getPartialInfoInLs(
        this.localStorageKey
      );
      if (!!existingStaff && existingStaff.length > 0 && !this.shownInApp) {
        staffList = staffList.map((st) => {
          const lsStaff = existingStaff.filter((sta) => st.id === sta.id)[0];
          if (!!lsStaff) {
            return lsStaff;
          }
          return st;
        });
      }
      this.allStaff = cloneDeep(staffList);
      this.dataSource = new MatTableDataSource(
        this.allStaff.filter((st) => !st.deleted)
      );
      if (this.dataSource.data.length === 0 && this.showAddAnother) {
        this.addNewRow();
      }
      this.activeStaff.emit(this.allStaff);
      this.validState.emit(this.dataSource.data.length > 0);
      this.createForm();
    });
  }

  //Adds new shift type and staff type with same name and returns the id of the new staff type created.
  addNewStaffType(data) {
    const newStaffTypeIndex = this.staffTypes.findIndex(staff => staff.stafftypeid === data.stafftypeid);
    const newStaffType = this.staffTypes[newStaffTypeIndex];
    return this._shiftTypesService.putShiftType([{
      name: newStaffType.abbreviation,
      staffTypeAbbreviations: [newStaffType.abbreviation],
      displayName: newStaffType.abbreviation,
      isActive: true
    } as any]).pipe(map(res => {
      const staffTypeIndex = res.findIndex(shiftType => shiftType.name === newStaffType.abbreviation)
      const addedStafftype = res[staffTypeIndex].staffTypes[0];
      const addedStafftypeId = addedStafftype.staffTypeId;
      this.staffTypes[newStaffTypeIndex] = { ...addedStafftype, stafftypeid: addedStafftypeId };
      data.stafftypeid = addedStafftypeId;
      return addedStafftypeId;
    }))
  }

  sortStaffList() {
    this.staffTypes.sort(function (a, b) {
      return a.abbreviation.localeCompare(b.abbreviation);
    });
  }
}

